import React, { useContext, useEffect, useState } from 'react';
import { ColumnProps } from 'antd/lib/table';

import LayoutContext from '@totem/components/devices/variables/presentation/layout/LayoutContext';
import SectionContext from '@totem/components/devices/variables/presentation/layout/layoutSection/SectionContext';
import { VariableDataResponseExtended } from '@totem/components/devices/variables/presentation/layout/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { isNotNull } from '@totem/utilities/common';
import {
  sortNumberAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

type Props = {
  purpose: string;
  maxDecimalPlaces: number;
};

const CurrentValuesWithStatusTable = ({ purpose, maxDecimalPlaces }: Props) => {
  const [localData, setLocalData] = useState<VariableDataResponseExtended[]>(
    [],
  );
  const { variableData, loading } = useContext(LayoutContext);
  const { sectionData } = useContext(SectionContext);

  useEffect(() => {
    if (isNotNull(sectionData) && isNotNull(sectionData.variables)) {
      const localVariables = sectionData.variables
        .filter((element) => element.metaData.purpose === purpose)
        .map((element) => element.variableId);

      if (isNotNull(variableData)) {
        const localVariableData = variableData.filter(
          (element) =>
            localVariables.findIndex((chk) => chk === element.id) >= 0,
        );
        setLocalData(localVariableData);
      }
    }
  }, [sectionData, variableData]);

  const calculateDataStatus = (chkValue: VariableDataResponseExtended) => {
    if (chkValue.history.length > 1) {
      if (
        chkValue.history[chkValue.history.length - 1].stringValue ===
          chkValue.stringValue &&
        chkValue.history[chkValue.history.length - 2].stringValue ===
          chkValue.stringValue
      ) {
        return <span style={{ color: '#FF0000' }}>STALE</span>;
      }
    }
    return <span>Current</span>;
  };

  const columns: ColumnProps<VariableDataResponseExtended>[] = [
    {
      title: 'Location',
      dataIndex: 'metaData.location',
      key: 'displayName',
      showSorterTooltip: true,
      width: '25%',
      render: (_, record: VariableDataResponseExtended) => (
        <span>{record.metaData.location}</span>
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.metaData.location, compB.metaData.location),
    },
    {
      title: purpose,
      dataIndex: 'value',
      key: 'value',
      showSorterTooltip: true,
      width: '35%',
      render: (_, record: VariableDataResponseExtended) => (
        <span>
          {record.isNumeric
            ? record.value.toFixed(maxDecimalPlaces)
            : record.stringValue}
        </span>
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) => {
        if (compA.isNumeric) {
          return sortNumberAscending(compA.value, compB.value);
        }
        return sortStringAscending(compA.stringValue, compB.stringValue);
      },
    },
    {
      title: 'Status of Data',
      dataIndex: 'id',
      key: 'id',
      width: '160px',
      render: (_, record: VariableDataResponseExtended) => (
        <span>{calculateDataStatus(record)}</span>
      ),
    },
    {
      title: 'BAS Point Name',
      dataIndex: 'label',
      key: 'label',
      render: (_, record: VariableDataResponseExtended) => (
        <span>{record.label}</span>
      ),
    },
  ];

  return (
    <div style={{ marginBottom: '16px' }}>
      {isNotNull(localData) && localData.length > 0 && (
        <IBTable
          showSorterTooltip
          columns={columns}
          dataSource={localData}
          loading={loading}
          pagination={{ hideOnSinglePage: true }}
          rowKey={(record) => record.id}
        />
      )}
    </div>
  );
};

export default CurrentValuesWithStatusTable;
